import { useEffect, useRef } from 'react'
import { useQuery, useInfiniteQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { useGeneratorStore } from '@/stores/generatorStore'
import type { AuthMeResponse } from '@/hooks/useAuthMe'
import type { VideoRatio } from '@/lib/constants'
import type { GenerationStatus } from '@/types/generation'
import type { ConfigResponse } from '@/hooks/useConfig'

export interface FrameImageInfo {
  role: 'first_frame' | 'last_frame'
  s3Key: string
  thumbnailS3Key: string
  filename?: string
}

export interface HistoryTemplateInfo {
  id: string
  name: string
  description: string | null
  /** 언어 코드 → 번역된 이름 (ko 원문은 name) */
  nameI18n?: Record<string, string> | null
  /** 언어 코드 → 번역된 설명 (ko 원문은 description) */
  descriptionI18n?: Record<string, string> | null
  thumbnailUrl: string | null
}

export interface GeneratedVideoInfo {
  s3Key: string
  thumbnailS3Keys: string[]
  durationSeconds: number
  videoUrl: string
  thumbnailUrls?: string[]
  thumbnailUrl?: string | null
}

export interface HistoryItem {
  id: string
  prompt: string
  ratio: VideoRatio
  resolution: string
  duration: number
  status: GenerationStatus
  taskPhase: string | null
  frameImages: FrameImageInfo[] | null
  frameImageUrls: string[] | null
  generatedVideo: GeneratedVideoInfo | null
  completedAt: string | null
  errorMessage: string | null
  createdAt: string
  template: HistoryTemplateInfo | null
}

export interface HistoryResponse {
  items: HistoryItem[]
  pagination: {
    page: number
    limit: number
    total: number
    hasMore: boolean
  }
}

export function useHistory(page: number = 1, limit: number = 20) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const userId = useAuthStore((state) => state.user?.id)

  return useQuery({
    queryKey: ['history', userId, page, limit],
    queryFn: () => api.get<HistoryResponse>(`/history?page=${page}&limit=${limit}`),
    enabled: isAuthenticated && !!userId,
    staleTime: 1000 * 60,
  })
}

export function useInfiniteHistory(limit: number = 10) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const userId = useAuthStore((state) => state.user?.id)

  const query = useInfiniteQuery({
    queryKey: ['history', 'infinite', userId, limit],
    queryFn: ({ pageParam }) => api.get<HistoryResponse>(`/history?page=${pageParam}&limit=${limit}`),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined,
    initialPageParam: 1,
    enabled: isAuthenticated && !!userId,
    staleTime: 1000 * 60,
  })

  // Auto-refetch every 5 seconds when there are processing items
  const processingCount = query.data?.pages.reduce(
    (acc, page) => acc + page.items.filter(item => item.status === 'processing').length,
    0
  ) ?? 0
  const hasProcessing = processingCount > 0

  const { refetch } = query

  useEffect(() => {
    if (!hasProcessing) return
    const interval = setInterval(() => refetch(), 5000)
    return () => clearInterval(interval)
  }, [hasProcessing, refetch])

  // 과금은 생성 '완료' 시점에 서버에서 일어나므로, processing 개수가 줄어들 때마다
  // /auth/me 로 워크스페이스 한도(hasUsage)를 재확인한다 (새로고침 없이 배너/버튼 반영).
  const queryClient = useQueryClient()
  const prevProcessingRef = useRef(processingCount)

  useEffect(() => {
    const prev = prevProcessingRef.current
    prevProcessingRef.current = processingCount
    if (!isAuthenticated || processingCount >= prev) return

    api
      .get<AuthMeResponse>('/auth/me')
      .then((me) => {
        queryClient.setQueryData(['auth', 'me'], me)
        const currentUser = useAuthStore.getState().user
        if (!currentUser || me.hasUsage === currentUser.hasUsage) return
        useAuthStore.getState().setUser({ ...currentUser, hasUsage: me.hasUsage })
        if (me.hasUsage === false) {
          useGeneratorStore.getState().setUsageLimitModalOpen(true)
        }
      })
      .catch(() => {
        // 한도 재확인 실패는 무시 — 다음 페이지 로드/생성 요청 시 다시 확인된다
      })
  }, [isAuthenticated, processingCount, queryClient])

  return query
}

export function useInvalidateHistory() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['history'] })
  }
}

export function useDeleteHistory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/history/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history'] })
    },
  })
}

export function useGenerateVideo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      const config = queryClient.getQueryData<ConfigResponse>(['config'])
      if (config?.outageNotice?.enabled) {
        throw new Error(config.outageNotice.message)
      }
      return api.post<{ id: string; status: string }>('/generate', params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history'] })
    },
  })
}

export function useCancelGeneration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      return api.post<{ success: boolean }>(`/generate/${id}/cancel`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history'] })
    },
  })
}
